import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { environment } from '../../environments/environment';
import { ToasterService } from "./toaster.service";
import { WebstorageService } from "./webstorage.service";
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { OktaAuth } from '@okta/okta-auth-js';
import { OKTA_AUTH } from "@okta/okta-angular";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { MsalService } from "@azure/msal-angular";
import * as _ from 'lodash';
import fileextension from "../../assets/meta/fileextenstion.json";
import { TokenStorage, Tokens, UserManager } from "@forgerock/javascript-sdk";
import * as Util from '../utils/utils';
import { InviteStatus } from "../secure/partners-user/models/partners-user.enum";
import { EventSourcePolyfill } from 'event-source-polyfill';
import { currentContextVersion } from './tab-session';

export enum EVENTSOURCE {
    INPROGRESS = "inprogress",
    SUCCESS = "success",
    ERROR = "error",
    INITIAL = "initial",
    OPEN = "open",
    CANCELLED = "cancelled"
}

/** How long a request that must report back (lists, organization pages) waits for an access token before failing instead of hanging. */
const TOKEN_WAIT_MS = 30000;

/**
 * A template's attribute list as an array. The engine returns it as stored: missing when the template has none
 * (so the response is `{}`), or as a JSON string (the engine's own download service parses that form too).
 */
function normalizeTemplateAttributes(value: any): any[] {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
}

@Injectable()
export class GatewayService {

    public selectedTenantID = environment._TenantID;
    public selectedPartnerID = environment._PartnerID;
    public baseURL = '';
    public clientID = '';
    public token = '';
    public loggedInUser = '';

    userUpdate = new BehaviorSubject({});
    userUpdateData = this.userUpdate.asObservable();

    tenantData = new Subject<any>();
    tenantDataObs = this.tenantData.asObservable();

    tenantDataRefresh = new Subject<any>();
    tenantDataRefreshObs = this.tenantDataRefresh.asObservable();

    updateLanguage = new Subject<any>();
    updateLanguageObs = this.updateLanguage.asObservable();

    isAcknowledgementEnabled = new BehaviorSubject<boolean>(false);
    isAcknowledgementEnabled$ = this.isAcknowledgementEnabled.asObservable();
    // Becomes true once the real acknowledgement setting has arrived (isAcknowledgementEnabled starts as a placeholder false).
    isAcknowledgementStatusLoaded = new BehaviorSubject<boolean>(false);
    isAcknowledgementStatusLoaded$ = this.isAcknowledgementStatusLoaded.asObservable();

    isManualReminderEnabled = new BehaviorSubject<boolean>(false);
    isManualReminderEnabled$ = this.isManualReminderEnabled.asObservable();

    isAutomationEnabled = new BehaviorSubject<boolean>(true);
    isAutomationEnabled$ = this.isAutomationEnabled.asObservable();

    isLatestChangeEnabled = new BehaviorSubject<boolean>(false);
    isLatestChangeEnabled$ = this.isLatestChangeEnabled.asObservable();

    isAddAdminAllowed = new BehaviorSubject<boolean>(false);
    isAddAdminAllowed$ = this.isAddAdminAllowed.asObservable();

    isSubOrganizationEnabled = new BehaviorSubject<boolean>(false);
    isSubOrganizationEnabled$ = this.isSubOrganizationEnabled.asObservable();

    isUserDownloadProgressBarEnabled = new BehaviorSubject<boolean>(false);
    isUserDownloadProgressBarEnabled$ = this.isUserDownloadProgressBarEnabled.asObservable();

    isBulkInviteEnabled = new BehaviorSubject<boolean>(false);
    isBulkInviteEnabled$ = this.isBulkInviteEnabled.asObservable();
    
    isBulkDeleteEnabled = new BehaviorSubject<boolean>(false);
    isBulkDeleteEnabled$ = this.isBulkDeleteEnabled.asObservable();

    isMultilingualHeaderSupported = new BehaviorSubject<boolean>(false);
    isMultilingualHeaderSupported$ = this.isMultilingualHeaderSupported.asObservable();

    isUserExpiryEnabled = new BehaviorSubject<boolean>(false);
    isUserExpiryEnabled$ = this.isUserExpiryEnabled.asObservable();
    /** Tenant attribute isPartnerScopeEnforced: on unless the tenant sets it to false. */
    isPartnerScopeEnforced = new BehaviorSubject<boolean>(true);
    isPartnerScopeEnforced$ = this.isPartnerScopeEnforced.asObservable();

    isInviteExpiryEnabled = new BehaviorSubject<boolean>(false);
    isInviteExpiryEnabled$ = this.isInviteExpiryEnabled.asObservable();

    neverExpireUser = new BehaviorSubject<boolean>(false);
    neverExpireUser$ = this.neverExpireUser.asObservable();

    neverExpireInvitation = new BehaviorSubject<boolean>(false);
    neverExpireInvitation$ = this.neverExpireInvitation.asObservable();

    dashboardObj = {
        parent: {
            to: null,
            from: null,
            selection: 'All',
            loading: true,
            isError: false
        },
        userSummary: {
            to: null,
            from: null,
            selection: null,
            loading: true,
            isError: false
        },
        groupSummary: {
            to: null,
            from: null,
            selection: null,
            loading: true,
            isError: false
        },
        usersChart: {
            to: null,
            from: null,
            selection: null,
            loading: true,
            isError: false
        },
        operationalSummary: {
            to: null,
            from: null,
            selection: null,
            loading: true,
            isError: false
        }
    }

    groupsFilterChange = new Subject();
    isGroupsFilterChange = this.groupsFilterChange.asObservable();

    groupsDataUpdated = new Subject();
    isGroupsDataUpdated = this.groupsDataUpdated.asObservable();


    constructor(public toast: ToasterService, public storage: WebstorageService, public http: HttpClient, public router: Router, @Inject(OKTA_AUTH) public oktaAuth: OktaAuth, private msalService: MsalService) {
        this.getUserDetails();
     }

     async getUserDetails() {
        if(environment.DIRECTORY === 'Azure') {
            if (!_.isNull(this.msalService.instance.getActiveAccount())) {
                const userDetails = this.msalService.instance.getActiveAccount();
                this.loggedInUser = userDetails.username;
             }
        } else if (environment.DIRECTORY === 'Okta') {
            this.oktaAuth.getUser().then((res)=> {
                this.loggedInUser = res.email;
            });
        } else if (environment.DIRECTORY === 'ForgeRock DS' ||  environment.DIRECTORY === 'ForgeRock_IM' || environment.DIRECTORY === 'ForgeRock_IC') {
            const userinfo = await UserManager.getCurrentUser();
            this.loggedInUser = userinfo["name"];
        }
    }

    public getHeader(accessToken) {
        let _headers;
        return _headers = new HttpHeaders().set('Authorization', 'Bearer ' + accessToken);
    }

    /**
     * Resolves with this tab's access token, unless the tab switched organization or signed out first: then it stays
     * pending, so a call started for the previous organization is never sent with the new organization's IDs.
     */
    getAccessToken(isTenantCall?): any {
        const version = currentContextVersion();
        return new Promise((resolve) => {
            this.resolveAccessToken(isTenantCall).then((token) => {
                if (currentContextVersion() === version) {
                    resolve(token);
                }
            });
        });
    }

    private resolveAccessToken(isTenantCall?): Promise<any> {
        return new Promise(async (resolve) => {
            if (environment.DIRECTORY === 'Okta') {
                //return this.oktaAuth.getAccessToken();
                this.token = this.oktaAuth.getAccessToken();
                if (isTenantCall) {
                    resolve(this.oktaAuth.getAccessToken());
                    return;
                }
                if (this.token && this.selectedTenantID) {
                    resolve(this.oktaAuth.getAccessToken());
                }
            } else if (environment.DIRECTORY === 'Azure') {
                this.msalService.instance.handleRedirectPromise().then(res => {
                    let tokenRequest = {
                        scopes: [],
                        clientId: environment.AD_CONFIG['azure']['redirectUri']
                    };
                    this.msalService.instance.acquireTokenSilent(tokenRequest).then((res) => {
                        this.token = res.accessToken;

                        if (isTenantCall) {
                            resolve(res.accessToken);
                        }

                        if (this.token && this.selectedTenantID) {
                            resolve(res.accessToken);
                        }
                    })
                })
            } else if (environment.DIRECTORY === 'ForgeRock DS' ||  environment.DIRECTORY === 'ForgeRock_IM' || environment.DIRECTORY === 'ForgeRock_IC') {
                const tokens: Tokens = await TokenStorage.get();
                this.token = tokens.idToken;

                if (isTenantCall) {
                    resolve(tokens.idToken);
                }
                if (this.token && this.selectedTenantID) {
                    resolve(tokens.idToken);
                }
            }
        })
    }

    setTenantId(resolve, token) {
        this.http.get(environment.PARTNERAPI + environment.TENANT_API,
            {
                headers: this.getHeader(this.token)
            }
        ).subscribe((response) => {
            if (response[0]) { this.toast.error("TOAST.ERROR_GETTING_TENANT_DATA"); }
            if (response[1]) {
                //const client = environment.TENANT_NAME;
                let client = (location.host).split(/:|\./)[0];

                if (Util.isLocalNetwork(client)) {
                    client = environment.CLIENT_FOR_LOCALHOST;
                }

                const tenantInfo = response[1].filter(el => el.tenantName.toLowerCase() === client);
                this.selectedTenantID = (tenantInfo.length) ? tenantInfo[0].tenantID : null;
                resolve(token);
            }
        }, (err) => {
            this.toast.error('TOAST.ERROR_GETTING_TENANT_DATA');
        }
        );
    }

    protected removeEmptyString(map: any) {           /* Remove the empty string parameter [Dynamo doesnot support empty string] */

        for (let key of Object.keys(map)) {

            if (map[key] === "" || map[key] === null) {
                delete map[key];
            }
            else if (map[key] != "" || map[key] != null) {
                var innerKey = map[key];
                if (typeof (innerKey) == "object") {
                    this.removeEmptyString(innerKey);
                }
            }
        }
    }

    public getTenant = function getTenant(callback): void {
        this.getAccessToken(true).then((token) => {
            let client = (location.host).split(/:|\./)[0];

            if (Util.isLocalNetwork(client) || client.includes('localhost')) {
                client = environment.CLIENT_FOR_LOCALHOST;
            }
            
            const organization = client ? `?organization=${client}`: '';
            
            this.http.get(environment.PARTNERAPI + environment.TENANT_API + organization,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(
                    (Response) => {
                        if (Response[0]) { 
                            this.toast.error("TOAST.ERROR_GETTING_TENANT_DATA"); 
                        } else if(Response[1] && Response[1].length > 0) {
                            const tenantData = Response[1][0];
                            const isManualReminderEnabled = tenantData?.isManualReminderEnabled || false;
                            this.isManualReminderEnabled.next(isManualReminderEnabled);
                        }
                        callback(null, Response[1]);
                    }
                    , err => callback(err, null)
                );
        });

    }

    /**
     * getAccessToken() never rejects: on a missing token or tenant, or a failed silent refresh, it stays pending.
     * A request whose page waits on it must always report back, or the page shows a loader forever, so this gives up after
     * TOKEN_WAIT_MS with an error shaped like an HTTP 401.
     */
    private getAccessTokenWithTimeout(): Promise<any> {
        const version = currentContextVersion();
        return new Promise((resolve, reject) => {
            const tokenError = (detail: string) => ({ name: "TokenError", status: 401, error: { status: 401, detail } });
            // After an organization switch the call belongs to a screen that is gone: report nothing.
            const timer = setTimeout(() => {
                if (currentContextVersion() === version) reject(tokenError("Timed out acquiring an access token."));
            }, TOKEN_WAIT_MS);
            Promise.resolve()
                .then(() => this.getAccessToken())
                .then(
                    (token) => { clearTimeout(timer); resolve(token); },
                    (err) => { clearTimeout(timer); reject(tokenError(err?.message || "Could not acquire an access token.")); }
                );
        });
    }

    public getAllPartnerUsers = function getAllPartnerUsers(callback, index: number = 0): void {

        const query = index ? `&startIndex=${index}` : '';

        this.getAccessTokenWithTimeout().then((token) => {
            this.http.get(
                `${environment.BASEAPI}/Users?tenantid=${this.selectedTenantID}&partnerId=${environment._PartnerID}${query}`,
                {
                    headers: this.getHeader(token)
                }
            )
                .subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null))

    }

    public getUserData = function getUserData(user, callback, advancedSearchFilter: any, index: number = 0): void {

        let queryFilter = "";
        if(advancedSearchFilter.value) {
            let filterExpression = `${advancedSearchFilter.attribute} ${advancedSearchFilter.condition}`;
            if (advancedSearchFilter.attribute === "active") {
                filterExpression += ` ${advancedSearchFilter.value}`;
            } else {
                filterExpression += ` "${advancedSearchFilter.value}"`
            }
            // Encoded so a value containing "+", "&", "#" or "%" (common in emails) reaches the server intact.
            queryFilter = `filter=${encodeURIComponent(filterExpression)}`;
        } else if (user) {
            queryFilter = `userName=${encodeURIComponent(user)}`
        }

        if (index) {
            queryFilter += `&startIndex=${index}`;
        }

        this.getAccessTokenWithTimeout().then((token) => {
            this.http.get(
                `${environment.BASEAPI}/Users?tenantid=${this.selectedTenantID}&partnerId=${environment._PartnerID}&${queryFilter}`,
                {
                    headers: this.getHeader(token)
                }
            )
                .subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null))

    }

    public getPartnerUserData = function getPartnerUserData(id, callback): void {

        this.getAccessToken().then((token) => {
            this.http.get(
                environment.BASEAPI + "/Users/" + id + "?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                {
                    headers: this.getHeader(token)
                }
            )
                .subscribe(Response => callback(null, Response), err => callback(err, null));
        })

    }


    /**
     * Endpoint URL and the automation token, encrypted to the page's public key (token-envelope.ts). Only the public key
     * and request id travel in the query string; ngsw-bypass keeps the service worker away from this credential response.
     */
    public getAutomationDetails = function getAutomationDetails(encryption: { tokenEncryption: string; recipientKey: string; requestId: string }, callback): void {
        this.getAccessTokenWithTimeout().then((token) => {
            const params = new HttpParams({ fromObject: { partnerId: environment._PartnerID, ...encryption, 'ngsw-bypass': 'true' } });
            this.http.get(
                environment.PARTNERAPI + environment.TENANT_API + "/" + this.selectedTenantID + "/automationdetails",
                {
                    headers: this.getHeader(token),
                    params
                }
            )

                .subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null))

    }

    getResourceTypes(token): Observable<any> {
        // if (environment.DIRECTORY === 'Okta') {

        // } else {
        //     return this.http.get(environment.BASEAPI + '/ResourceTypes?tenantid=' + this.selectedTenantID, { headers: new HttpHeaders().set('Authorization', 'Bearer ' + token) })
        // }

        return this.http.get(environment.BASEAPI + '/ResourceTypes?tenantid=' + this.selectedTenantID + "&partnerId=" + environment._PartnerID, { headers: new HttpHeaders().set('Authorization', 'Bearer ' + token) })

    }

    getSchemas(token): Observable<any> {
        // if (environment.DIRECTORY === 'Okta') {

        // } else {
        //     return this.http.get(environment.BASEAPI + '/Schemas?tenantid=' + this.selectedTenantID, { headers: new HttpHeaders().set('Authorization', 'Bearer ' + token) })
        // }

        return this.http.get(environment.BASEAPI + '/Schemas?tenantid=' + this.selectedTenantID + "&partnerId=" + environment._PartnerID, { headers: new HttpHeaders().set('Authorization', 'Bearer ' + token) })

    }

    // Resolves with `attributes_and_expressions` always an array (see normalizeTemplateAttributes).
    public getSelectedAttributesFromTemplate = function getSelectedAttributesFromTemplate(callback): void {
        this.getAccessTokenWithTimeout().then((token) => {
            this.http.get(
                environment.BASEAPI + "/Attributes?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                {
                    headers: this.getHeader(token)
                }
            )
                .subscribe(
                    Response => callback(null, { ...Response, attributes_and_expressions: normalizeTemplateAttributes(Response?.attributes_and_expressions) }),
                    err => callback(err, null)
                );
        }, err => callback(err, null))
    }

    public getSelectedGroupsFromTemplate = function getSelectedGroupsFromTemplate(callback): void {
        this.getAccessToken().then((token) => {
            this.http.get(
                environment.BASEAPI + "/Groups?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                {
                    headers: this.getHeader(token)
                }
            )
                .subscribe(Response => callback(null, Response), err => callback(err, null));
        })
    }

    public createUser = function createUser(body: any, callback): void {
        this.getAccessToken().then((token) => {
            this.http.post(
                environment.BASEAPI + "/Users?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                body,
                {
                    headers: this.getHeader(token)
                }
            )
                .subscribe(Response => callback(Response[0], Response[1]), err => callback(err, null));
        })
    }

    public updateUser = function updateUser(id, body: any, callback): void {
        this.getAccessToken().then((token) => {
            this.http.put(
                environment.BASEAPI + "/Users/" + id + "?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                body,
                {
                    headers: this.getHeader(token)
                }
            )
                .subscribe(Response => callback(Response[0], Response[1]), err => callback(err, null));
        })
    }

    public deleteUser = function deleteUser(callback, userId: string): void {
        this.getAccessToken().then((token) => {
            this.http.delete(
                environment.BASEAPI + "/Users/" + userId + "?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                {
                    headers: this.getHeader(token)
                }
            )
                .subscribe(Response => callback(null, Response), err => callback(err, null));
        })
    }

    public initiateDownloadCsv = function initiateDownloadCsv(callback): void {
        this.getAccessToken().then((token) => {
            this.http.get(
                environment.BASEAPI + "/Users/download/initiate?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                {
                    headers: this.getHeader(token)
                }
            )
                .subscribe(Response => callback(null, Response), err => callback(err, null));
        })
    }

    public cancelDownload = function cancelDownload(callback): void {
        this.getAccessToken().then((token) => {
            this.http.get(
                environment.BASEAPI + "/Users/download/stop?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                {
                    responseType: "text/csv",
                    headers: this.getHeader(token)
                }
            )
                .subscribe(Response => callback(null, Response), err => callback(err, null));
        })
    }

    /** Streams the export status. The returned handle closes the stream, also if it has not opened yet. */
    public getDownloadStatus = function getDownloadStatus(callback): { close: () => void } {
        let source: EventSourcePolyfill | null = null;
        let closed = false;
        this.getAccessToken().then((token) => {
            if (closed) return;
            if (typeof (EventSource) !== undefined) {
                // The access token goes in the Authorization header, never in the URL (URLs end up in logs and history).
                source = new EventSourcePolyfill(environment.BASEAPI + "/Users/download/status?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID + "&ngsw-bypass=true", {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                source.addEventListener('message', (event: any) => {
                    let responseData = event.data.toLowerCase().trim();
                    if (responseData.includes(EVENTSOURCE.SUCCESS) || responseData === EVENTSOURCE.CANCELLED) {
                        source.close();
                    }
                    callback(null, responseData);
                });
                source.addEventListener('error', (event) => {
                    source.close();
                    callback(event, null);
                });
            } else {
                callback('Download Users Failed', null);
            }
        })
        return {
            close: () => {
                closed = true;
                source?.close();
            }
        };
    }

    public downloadUsersCSV = function downloadUsersCSV(callback) {
        this.getAccessToken().then((token) => {
            this.http.get(
                environment.BASEAPI + "/Users/download?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                {
                    responseType: "text/csv",
                    headers: this.getHeader(token)
                }
            )
                .subscribe(Response => callback(null, Response), err => callback(err, null));
        })
    }

    public uploadUserData = function uploadUserData(file, selectedFile, fileName, progress, callback) {

        this.getAccessToken().then((token) => {

            let extension = "." + (selectedFile.name.split('.').pop()).toLowerCase();
            let selectedFileType = this.getFileType(extension, selectedFile);

            this.http.post(
                environment.BASEAPI + "/Users/upload?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                { type: selectedFileType, file: file, fileName: fileName },
                {
                    headers: new HttpHeaders({
                        "Authorization": "Bearer " + token,
                        "ngsw-bypass": "true"
                    }),
                    reportProgress: true,
                    observe: "events"
                }
            )


                .subscribe((event: HttpEvent<any>) => {
                    if (event.type === HttpEventType.UploadProgress) {
                        // This is an upload progress event. Compute and show the % done:
                        progress["percentage"] = Math.round(100 * event.loaded / event.total);
                    } else if (event instanceof HttpResponse) {
                        progress.percentage = 100;
                        callback(null, event);
                    }
                }, err => callback(err, null));

        });
    }

    private getFileType(extension, selectedFile) {
        let filter: any = fileextension.filter(file => file.extension === extension);

        if (filter.length > 0 && filter[0].type !== selectedFile.type) {                 // FIX: windows msexcel opens csv file with ectension as XL file reader
            return filter[0].type;
        } else {
            return selectedFile.type;
        }
    }

    public bulkUploadUserData = function bulkUploadUserData(fileBase64, selectedFile, fileName, progress, type, callback) {
        let extension = "." + (selectedFile.name.split('.').pop()).toLowerCase();
        let selectedFileType = this.getFileType(extension, selectedFile);

        this.getAccessToken().then((token) => {
            this.http.post(
                `${environment.BASEAPI}/Tenants/${this.selectedTenantID}/partners/${environment._PartnerID}/Bulk/${type}`,
                { type: selectedFileType, file: fileBase64, fileName },
                {
                    headers: new HttpHeaders({
                        "Authorization": "Bearer " + token,
                        "ngsw-bypass": "true"
                    }),
                    reportProgress: true,
                    observe: "events"
                },
            )
            .subscribe((event: HttpEvent<any>) => {
                if (event.type === HttpEventType.UploadProgress) {
                    // This is an upload progress event. Compute and show the % done:
                    progress["percentage"] = Math.round(100 * event.loaded / event.total);
                } else if (event instanceof HttpResponse) {
                    progress.percentage = 100;
                    callback(null, event);
                }
            }, err => callback(err, null));
        })
    }

    public getUploadHistroy = function getUploadHistroy(callback): void {
        this.getAccessToken().then((token) => {
            this.http.get(
                environment.BASEAPI + "/users/upload/history?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                {
                    headers: this.getHeader(token)
                }
            )
                .subscribe(Response => callback(null, Response), err => callback(err, null));
        })
    }

    public getUsersUploadHistory = function getUsersUploadHistory(callback, operation: string): void {
        this.getAccessToken().then((token) => {
            this.http.get(
                `${environment.BASEAPI}/Tenants/${this.selectedTenantID}/partners/${environment._PartnerID}/Upload/history?operation=${operation}`,
                {
                    headers: this.getHeader(token)
                }
            )
            .subscribe(Response => callback(null, Response), err => callback(err, null));
        })
    }

    public getUploadHistroyById = function getUploadHistroyById(reqObj, callback): void {
        let queryParams = "";
        if (reqObj?.id) {
            queryParams += "/" + reqObj.id + "?";
        }
        if (reqObj?.startIndex) {
            queryParams += "startIndex=" + reqObj.startIndex + "&count=50&"
        }
        this.getAccessToken().then((token) => {
            this.http.get(
                environment.BASEAPI + "/users/upload/history" + queryParams + "tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                {
                    headers: this.getHeader(token)
                }
            )
                .subscribe(Response => callback(null, Response), err => callback(err, null));
        })
    }

    public getUsersUploadHistroyById = function getUsersUploadHistroyById(reqObj, callback): void {
        let params = new HttpParams();

        if (reqObj?.startIndex) {
            params = params.set("startIndex", reqObj.startIndex);
            params = params.set("count", 50);
        }

        this.getAccessToken().then((token) => {
            this.http.get(
                `${environment.BASEAPI}/Tenants/${this.selectedTenantID}/partners/${environment._PartnerID}/Upload/status/${reqObj.id}`,
                {
                    headers: this.getHeader(token),
                    params: params,
                }
            )
            .subscribe(Response => callback(null, Response), err => callback(err, null));
        })
    }

    public getUploadHistoryStatus = function getUploadHistoryStatus(fileId, callback) {
        this.getAccessToken().then((token) => {
            this.http.get(
                `${environment.BASEAPI}/users/upload/history/usersstatus/${fileId}?tenantid=${this.selectedTenantID}&partnerId=${environment._PartnerID}`,
                {
                    responseType: "text/csv",
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        })
    }

    getAttributeValues(callback) {
        this.getAccessToken().then((token) => {
            this.http.get(
                environment.BASEAPI + "/PartnerDetails?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                {
                    headers: this.getHeader(token)
                }


            )
                .subscribe(Response => callback(null, Response), err => callback(err, null));
        })
    }

    // Dashboard 

    getPartnerDetails(callback, startTime?, endTime?) {
        this.getAccessToken().then(accessToken => {
            let queryString = '';
            if (startTime && endTime) {
                queryString = '?startTime=' + startTime + '&endTime=' + endTime;
            }
            let url = `${environment.PARTNERAPI}/Tenants/${this.selectedTenantID}/partners/${environment._PartnerID}/dashboard/joiningdetails`

            return this.http.get(url,
                {
                    headers: this.getHeader(accessToken)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        });
    }

    getUsersSummaryData(callback, startTime, endTime) {
        this.getAccessToken().then(accessToken => {
            let queryString = '';
            if (startTime && endTime) {
                queryString = '&startTime=' + startTime + '&endTime=' + endTime;
            }
            let url = `${environment.PARTNERAPI}/Tenants/${this.selectedTenantID}/partners/${environment._PartnerID}/dashboard/userssummary`;
            return this.http.get(url,
                {
                    headers: this.getHeader(accessToken)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        });
    }

    getOperationalSummaryData(callback, startTime?, endTime?) {
        this.getAccessToken().then(accessToken => {
            let queryString = '';
            if (startTime && endTime) {
                queryString = '&startTime=' + startTime + '&endTime=' + endTime;
            }
            let url = `${environment.PARTNERAPI}/Tenants/${this.selectedTenantID}/partners/${environment._PartnerID}/dashboard/operationalsummary?${queryString}`;
            
            return this.http.get(url,
                {
                    headers: this.getHeader(accessToken)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        });
    }

    getUsersGraphsSummaryData(callback, startTime?, endTime?, isDateRangeChangeFromDailog?){
        this.getAccessToken().then(accessToken => {
            let queryString = '';
            if (startTime && endTime) {
                queryString = '&startTime=' + startTime + '&endTime=' + endTime;
            }
            let url = `${environment.PARTNERAPI}/Tenants/${this.selectedTenantID}/partners/${environment._PartnerID}/dashboard/usersgraphssummary?${queryString}`;
           
            return this.http.get(url,
                {
                    headers: this.getHeader(accessToken)
                }
            ).subscribe(Response => callback(null, Response, isDateRangeChangeFromDailog), err => callback(err, null, isDateRangeChangeFromDailog));
        });
    }

    public getAdminsList = function getAdminsList(callback): void {
        this.getAccessTokenWithTimeout().then((token) => {
            this.http.get(
                environment.BASEAPI + "/admins?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                {
                    headers: this.getHeader(token)
                }
            )
            .subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null));
    }

    public addAdminToList = function addAdminToList(body, callback): void {
        this.getAccessTokenWithTimeout().then((token) => {
            this.http.put(
                environment.BASEAPI + "/admins?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                body,
                {
                    headers: this.getHeader(token)
                }
            )
            .subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null));
    }

    public invite = function invite(body, callback): void {
        this.getAccessToken().then((token) => {
            this.http.put(
                environment.BASEAPI + "/admins?tenantid=" + this.selectedTenantID + "&partnerId=" + environment._PartnerID,
                body,
                {
                    headers: this.getHeader(token)
                }
            )
            .subscribe(Response => callback(null, Response), err => callback(err, null));
        })
    }

    public getSubOrganizations = function getSubOrganizations(callback, pageId: string = ''): void {
        const queryParams = new URLSearchParams();
    
        if (pageId) {
            queryParams.append('pageId', pageId);
        }
    
        const query = queryParams.toString() ? `&${queryParams.toString()}` : '';

        this.getAccessTokenWithTimeout().then((token) => {
            this.http.get(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/suborganization/${environment._PartnerID}/listsuborganizations?partnerId=${environment._PartnerID}${query}`,
                {
                    headers: this.getHeader(token)
                }
            )
            .subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null))
    }

    public createPartner = function createPartner(partnerData: any, callback): void {
        this.getAccessTokenWithTimeout().then((accessToken => {
            partnerData.tenantId = environment._TenantID;
            partnerData.createdOn = partnerData.updatedOn = new Date().toISOString();
            partnerData.createdBy = partnerData.updatedBy = this.loggedInUser;
            this.http.post(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/suborganization?partnerId=${environment._PartnerID}`,
                partnerData,
                {
                    headers: this.getHeader(accessToken)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        }), err => callback(err, null));

    }

    public updatePartner = function updatePartner(partnerData: any, callback): void {
        this.getAccessTokenWithTimeout().then((token) => {
            partnerData.tenantId = environment._TenantID;
            partnerData.updatedOn = new Date().toISOString();
            partnerData.updatedBy = this.loggedInUser;
            this.http.put(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/suborganization/${partnerData.id}?partnerId=${environment._PartnerID}`,
                partnerData,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null));
    }

    public getTemplateDetails = function getTemplateDetails(callback): void {
        this.getAccessTokenWithTimeout().then((token) => {
            this.http.get(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/suborganization/${environment._PartnerID}/template?partnerId=${environment._PartnerID}`,
                {
                    headers: this.getHeader(token)
                }
            )
            .subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null));
    }

    public getSubOrganizationById = function getSubOrganizationById(partnerId ,callback): void {
        this.getAccessTokenWithTimeout().then((token) => {
            this.http.get(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/suborganization/${partnerId}?partnerId=${environment._PartnerID}`,
                {
                    headers: this.getHeader(token)
                }
            )
            .subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null));
    }

    public deleteSubOrganization = function deleteSubOrganization(organizationId: any, callback): void {
        this.getAccessTokenWithTimeout().then((token) => {
            this.http.delete(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/suborganization/${organizationId}?partnerId=${environment._PartnerID}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null));
    }

    public uploadOrganizationLogos = function uploadOrganizationLogos(logoData, callback): void{
        this.getAccessTokenWithTimeout().then(token => {
            this.http.post(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/suborganization/logos`,
                logoData,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null));
    }

    public deleteOrganizationLogos = function deleteOrganizationLogos(logoData, callback): void {
        this.getAccessTokenWithTimeout().then(token => {
            const url = `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/suborganization/logos`;
            const options = {
                headers: this.getHeader(token),
                body: logoData
            };
    
            this.http.request('DELETE', url, options).subscribe(
                response => callback(null, response),
                error => callback(error, null)
            );
        }, err => callback(err, null));
    }

    public checkUserInDirectory = function checkUserInDirectory(data, callback): void{
        this.getAccessTokenWithTimeout().then(token => {
            this.http.get(
                `${environment.BASEAPI}/admin?tenantid=${this.selectedTenantID}&partnerId=${data.organizationId}&email=${encodeURIComponent(data.email)}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null));
    }

    public createUserInDirectory = function createUserInDirectory(organizationId, reqPayload, callback): void{
        this.getAccessTokenWithTimeout().then(token => {
            this.http.post(
                `${environment.BASEAPI}/admin?tenantid=${this.selectedTenantID}&partnerId=${organizationId}`,
                reqPayload,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null));
    }

    public checkUserOfCurrentOrganizationInDirectory = function checkUserInDirectory(data, callback): void{
        this.getAccessTokenWithTimeout().then(token => {
            this.http.get(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/suborganization/${environment._PartnerID}/admins?email=${encodeURIComponent(data.email)}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null));
    }

    public createUserOfCurrentOrganizationInDirectory = function createUserInDirectory(data, callback): void {
        this.getAccessTokenWithTimeout().then(token => {
            this.http.post(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/suborganization/${environment._PartnerID}/admins`,
                data,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null));
    }

    public inviteUserToDirectory = function inviteUserToDirectory(data, callback): void {
        this.getAccessTokenWithTimeout().then(token => {
            this.http.post(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/admin`,
                data,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        }, err => callback(err, null));
    }
    
    getAcceptedUsers(callback, pageId?: string, searchValue?: string, timeRange?: { startTime: string, endTime: string }, isDownload?: boolean): void {

        const queryParams = new URLSearchParams();
    
        if (pageId) {
            queryParams.append('pageId', pageId);
        }
        
        if (searchValue) {
            queryParams.append('userName', searchValue);
        }

        if (timeRange) {
            queryParams.append('startTime', timeRange.startTime);
            queryParams.append('endTime', timeRange.endTime);
        }
        
        if (isDownload) {
            queryParams.append('isDownload', 'true');
        }

        const query = queryParams.toString() ? `?${queryParams.toString()}` : '';

        this.getAccessToken().then(token => {
            this.http.get(
                `${timeRange ? environment.BASEAPI : environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/${timeRange ? 'Invitee/accepted-users/filter' : 'accepted-users'}${query}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        });
    }

    getInvitedUsers(callback, pageId?: string, searchValue?: string, timeRange?: { startTime: string, endTime: string }, isDownload?: boolean): void {

        const queryParams = new URLSearchParams();
    
        if (pageId) {
            queryParams.append('pageId', pageId);
        }
        
        if (searchValue) {
            queryParams.append('userName', searchValue);
        }

        if (timeRange) {
            queryParams.append('startTime', timeRange.startTime);
            queryParams.append('endTime', timeRange.endTime);
        }

        if (isDownload) {
            queryParams.append('isDownload', 'true');
        }
    
        const query = queryParams.toString() ? `?${queryParams.toString()}` : '';

        this.getAccessToken().then(token => {
            this.http.get(
                `${timeRange ? environment.BASEAPI : environment.PARTNERAPI }${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/${timeRange ? 'Invitee/invited-users/filter' : 'invited-users'}${query}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        });
    }

    cancelInvitation(cb, userData): void {
        this.getAccessToken().then(token => {
            this.http.put(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/invitee/${userData.id}`,
                {
                    ...userData,
                    inviteStatus: InviteStatus.CANCELLED
                },
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    deleteNewUser(cb, userId: string): void {
        this.getAccessToken().then(token => {
            this.http.delete(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/invitee/${userId}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    resendInviteToNewUser(cb, data): void {
        this.getAccessToken().then(token => {
            this.http.put(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/invitee/${data.id}/resend-invite`,
                data,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    getPartiallyOnboardedUserById(userId: string, cb): void {
        this.getAccessToken().then(token => {
            this.http.get(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/invitee/${userId}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    getAcknowledgementEnabledStatus(cb): void {
        this.getAccessToken().then(token => {
            this.http.get(
                `${environment.BASEAPI}/Privacy-Notice-Data?tenantid=${this.selectedTenantID}&partnerId=${environment._PartnerID}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    inviteBulkUsers(cb, data): void {
        this.getAccessToken().then(token => {
            this.http.post(
                `${environment.BASEAPI}/Invite?tenantid=${this.selectedTenantID}&partnerId=${environment._PartnerID}`,
                data,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    getInvitedUsersData(cb): void {
        this.getAccessToken().then(token => {
            this.http.get(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/invitee/invited-users`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    getAcceptedUsersData(cb): void {
        this.getAccessToken().then(token => {
            this.http.get(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/invitee/accepted-users`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    getUsersUploadTemplate(cb): void {
        this.getAccessToken().then(token => {
            this.http.get(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/invitee/users-template`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    updateUserInviteStatus(userData, cb): void {
        this.getAccessToken().then(token => {
            this.http.put(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/invitee/${userData.id}`,
                userData,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    getAllInvitedUsers(cb, pageId?: string, searchValue?: string, advancedSearchFilter?: any): void {
        let params = new HttpParams();
        let isAdvancedSearch = false;

        if (pageId) {
          params = params.set('pageId', pageId);
        }
      
        if (advancedSearchFilter?.value || advancedSearchFilter?.attribute) {
            isAdvancedSearch = true;
            params = params.set(`${advancedSearchFilter.attribute}_${advancedSearchFilter.condition}`, advancedSearchFilter.value);
        }

        if (searchValue) {
            params = params.set('userName', searchValue);
        }

        this.getAccessToken().then(token => {
            this.http.get(
                `${isAdvancedSearch ? environment.BASEAPI : environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/${isAdvancedSearch ? 'Invitee/all-users/filter' : 'all-invitee'}`,
                {
                    headers: this.getHeader(token),
                    params: params
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    getAdvancedSearchFilteredUsersDownloadData(cb, advancedSearchFilter: any) {
        const query = `${advancedSearchFilter.attribute}_${advancedSearchFilter.condition}=${advancedSearchFilter.value}`;

        this.getAccessToken().then(token => {
            this.http.get(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/Invitee/all-users/filter?${query}&isDownload=true`,
                {
                    headers: this.getHeader(token),
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    getAllInvitedUsersDownloadData(cb): void {
        this.getAccessToken().then(token => {
            this.http.get(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/invitee/all-users`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    getAllInvitedUsersAuditLogs(cb, pageId?: string, searchValue?: string): void {
        const queryParams = new URLSearchParams();
    
        if (pageId) {
            queryParams.append('pageId', pageId);
        }
        
        if (searchValue) {
            queryParams.append('userName', searchValue);
        }
    
        const query = queryParams.toString() ? `?${queryParams.toString()}` : '';

        this.getAccessToken().then(token => {
            this.http.get(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/invitee/auditLogs${query}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    getPreviewEmail(cb, emailTemplateId: string): void {
        this.getAccessToken().then(token => {
            this.http.get(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/invitee/auditLogs/${emailTemplateId}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    validateEmail(cb, email: string): void {
        this.getAccessToken().then(token => {
            this.http.get(
                `${environment.BASEAPI}/Manager?tenantid=${this.selectedTenantID}&partnerId=${environment._PartnerID}&email=${email}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    getLogs(cb, pageId?: string, searchValue?: string, timeRange?: { startTime: string, endTime: string }): void {
        const queryParams = new URLSearchParams();
        const limit = 50;
        const pageTime = "30m";

        if (pageId) {
            queryParams.append('pageId', pageId);
        }
        
        if (searchValue) {
            queryParams.append('query', searchValue);
        }

        if (timeRange) {
            queryParams.append('startTime', timeRange.startTime);
            queryParams.append('endTime', timeRange.endTime);
        }

        queryParams.append('limit', limit.toString());
        queryParams.append('pageTime', pageTime);
    
        const query = queryParams.toString() ? `?${queryParams.toString()}` : '';

        this.getAccessTokenWithTimeout().then(token => {
            this.http.get(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/audit/logs${query}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        }, err => cb(err, null));
    }

    downloadLogs(cb, timeRange?: { startTime: string, endTime: string }, searchValue?: string): void {
        const queryParams = new URLSearchParams();

        if (timeRange) {
            queryParams.append('startTime', timeRange.startTime);
            queryParams.append('endTime', timeRange.endTime);
        }

        if (searchValue) {
            queryParams.append('query', searchValue);
        }

        const query = queryParams.toString() ? `?${queryParams.toString()}` : '';

        this.getAccessTokenWithTimeout().then(token => {
            this.http.get(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/audit/logs/download${query}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        }, err => cb(err, null));
    }

    downloadLogsWithFilters(cb, filterParams?: { attribute: string, attributeCondition: string, attributeValue: string }, timeRange?: { startTime: string, endTime: string }): void {
        const queryParams = new URLSearchParams();

        if (filterParams) {
            queryParams.append('attribute', filterParams.attribute);
            queryParams.append('attributeCondition', filterParams.attributeCondition);
            // URLSearchParams already encodes; encoding first too sent "a@b.com" as "a%2540b.com", which the
            // engine (it decodes once) searched for literally.
            queryParams.append('attributeValue', filterParams.attributeValue);
        }

        if (timeRange) {
            queryParams.append('startTime', timeRange.startTime);
            queryParams.append('endTime', timeRange.endTime);
        }

        const query = queryParams.toString() ? `?${queryParams.toString()}` : '';

        this.getAccessTokenWithTimeout().then(token => {
            this.http.get(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/audit/logs/filters/download${query}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        }, err => cb(err, null));
    }

    getLogsWithFilters(cb, pageId?: string, filterParams?: { attribute: string, attributeCondition: string, attributeValue: string }, timeRange?: { startTime: string, endTime: string }): void {
        const queryParams = new URLSearchParams();
        const limit = 50;
        const pageTime = "30m";

        if (pageId) {
            queryParams.append('pageId', pageId);
        }

        if (filterParams) {
            queryParams.append('attribute', filterParams.attribute);
            queryParams.append('attributeCondition', filterParams.attributeCondition);
            queryParams.append('attributeValue', encodeURIComponent(filterParams.attributeValue));
        }

        if (timeRange) {
            queryParams.append('startTime', timeRange.startTime);
            queryParams.append('endTime', timeRange.endTime);
        }

        queryParams.append('limit', limit.toString());
        queryParams.append('pageTime', pageTime);
    
        const query = queryParams.toString() ? `?${queryParams.toString()}` : '';

        this.getAccessTokenWithTimeout().then(token => {
            this.http.get(
                `${environment.PARTNERAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/audit/logs/filters${query}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        }, err => cb(err, null));
    }

    deleteInvitedUsers(cb, payload: any): void {
        this.getAccessToken().then(token => {
            this.http.post(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/Bulk/delete-invite`,
                payload,
                { headers: this.getHeader(token) }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    bulkInviteUsers(cb, payload: any): void {
        this.getAccessToken().then(token => {
            this.http.post(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/Bulk/invite`,
                payload,
                { headers: this.getHeader(token) }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    sendReminderToSelectedUsers(cb, payload: any, type: string): void {
        this.getAccessToken().then(token => {
            this.http.post(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/Notification/${type === 'reminder' ? 'bulk-reminder' : 'bulk-reinvite'}`,
                payload,
                { headers: this.getHeader(token) }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    bulkActivateOrDeactivateUsers(cb, payload: any, action: boolean): void {
        this.getAccessToken().then(token => {
            this.http.post(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/Bulk/deactivate?action=${action}`,
                payload,
                { headers: this.getHeader(token) }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    getAdvancedSearchFilters(callback) {
        this.getAccessToken().then((token) => {
            this.http.get(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/FilterAttributes`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        })
    }

    getAdvancedSearchFilteredUsers(cb, filterParams: any): void {
        let queryFilter = `filter=${filterParams.attribute} ${filterParams.condition}`;

        if (filterParams.attribute === "active") {
            queryFilter += ` ${filterParams.value}`;
        } else {
            queryFilter += ` "${filterParams.value}"`
        }

        this.getAccessToken().then(token => {
            this.http.get(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/Users/download?${queryFilter}`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => cb(null, Response), err => cb(err, null));
        });
    }

    getAllUsersFilterAttributes(callback) {
        this.getAccessToken().then((token) => {
            this.http.get(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/FilterAttributes/all-users`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        })
    }

    updateUserLogin() {
        this.getAccessToken().then(token => {
            this.http.post(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/audit/login`,
                {},
                { headers: this.getHeader(token) }
            ).subscribe();
        });
    }

    /** Opens the users-export progress stream. The returned handle closes it, even if it has not opened yet. */
    getUsersProcessing(handleResponse, operation: string, filterParams?: any): { close: () => void } {
        let queryFilter = '';

        if (filterParams) {
            let filterExpression = `${filterParams.attribute} ${filterParams.condition}`;

            if (filterParams.attribute === "active") {
                filterExpression += ` ${filterParams.value}`;
            } else {
                filterExpression += ` "${filterParams.value}"`;
            }
            // Encoded so a value containing "+", "&", "#" or "%" (common in emails) reaches the server intact.
            queryFilter = `filter=${encodeURIComponent(filterExpression)}`;
        }

        let eventSource: EventSourcePolyfill | null = null;
        let closed = false;

        this.getAccessToken().then((token) => {
            if (closed) return;
            const url = `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/Users/downloads?operation=${operation}${queryFilter ? `&${queryFilter}` : ''}`;
            
            eventSource = new EventSourcePolyfill(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            eventSource.onmessage = (event) => handleResponse(event);

            eventSource.addEventListener('complete', (event) => {
                handleResponse(event);
                eventSource.close();
            });

            eventSource.addEventListener('stopped', (event) => {
                handleResponse(event);
                eventSource.close();
            });
              
            eventSource.onerror = (event) => {
                handleResponse(event);
                eventSource.close();
            };
        })

        return {
            close: () => {
                closed = true;
                eventSource?.close();
            }
        };
    }

    getUsersDownloadData(callback): void {
        this.getAccessToken().then((token) => {
            this.http.get(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/Users/downloads?operation=download`,
                {
                    headers: this.getHeader(token)
                }
            )
            .subscribe(Response => callback(null, Response), err => callback(err, null));
        })
    }

    getUploadDocumentation(callback): void {
        this.getAccessToken(true).then((token) => {
            this.http.get(
                `${environment.BASEAPI}${environment.TENANT_API}/${this.selectedTenantID}/partners/${environment._PartnerID}/FilterAttributes/csv-docs`,
                {
                    headers: this.getHeader(token)
                }
            ).subscribe(Response => callback(null, Response), err => callback(err, null));
        });
    }
}
